import {
  Activity,
  BarChart3,
  Bot,
  Boxes,
  ClipboardCheck,
  Copy,
  FileText,
  Gauge,
  House,
  KeyRound,
  Trophy,
  MessageSquare,
  Music2,
  Palette,
  Printer,
  Radio,
  Search,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Users,
  Video,
  Volume2,
  WandSparkles,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { REBUILD_NAVIGATION, REBUILD_ROUTE_LABELS, type RebuildRouteId } from './navigation';
import './rebuild-shell.css';
import './sidebar-density.css';
import './post-rebuild-contract.css';
import './ffsdev-top-tabs.css';

type Role = 'owner' | 'operator';

type RebuildShellProps = {
  activeRoute: RebuildRouteId;
  role: Role;
  identityLabel: string;
  streaming: boolean;
  currentScene?: string | null;
  currentWamCategory?: string | null;
  nowPlaying?: string | null;
  clockLabel: string;
  dateLabel: string;
  children: ReactNode;
  sidebarUtility?: ReactNode;
  onNavigate: (route: RebuildRouteId) => void;
  onLogout?: () => void;
};

const ICONS: Record<RebuildRouteId, ComponentType<{ size?: number }>> = {
  home: House,
  'start-stream': Gauge,
  themes: Palette,
  'weekend-prep': ClipboardCheck,
  obs: Video,
  reports: BarChart3,
  'raid-finder': Search,
  viewers: Users,
  loyalty: Trophy,
  welcomes: Sparkles,
  alerts: Activity,
  sounds: Volume2,
  printer: Printer,
  'text-outputs': FileText,
  wam: Music2,
  'chat-bot': Bot,
  commands: MessageSquare,
  automations: WandSparkles,
  'system-health': ShieldCheck,
  logs: ScrollText,
  integrations: Radio,
  modules: Boxes,
  access: KeyRound,
};

export function RebuildShell({
  activeRoute,
  role,
  identityLabel,
  streaming,
  currentScene,
  currentWamCategory,
  nowPlaying,
  clockLabel,
  dateLabel,
  children,
  sidebarUtility,
  onNavigate,
  onLogout,
}: RebuildShellProps) {
  const activeTitle = REBUILD_ROUTE_LABELS[activeRoute];
  const activeGroup = REBUILD_NAVIGATION.find((group) => group.items.some((item) => item.id === activeRoute))?.label ?? 'Control';

  async function copyWamCategory() {
    const category = currentWamCategory?.trim();
    if (!category) return;
    try {
      await navigator.clipboard.writeText(category);
    } catch {
      window.prompt('Copy WAM category', category);
    }
  }

  return (
    <div className="ffs-next-shell">
      <header className="ffs-next-header">
        <div
          className="ffs-next-brand"
          aria-label="MutedFFS. Are you ready to connect? FFSControl"
        >
          <img
            className="ffs-next-mutedffs-logo"
            src="/brand/mutedffs-logo.png"
            alt="MutedFFS"
          />
          <div className="ffs-next-brand-copy">
            <div className="ffs-next-wordmark" aria-label="FFSControl">
              <span className="ffs-next-brand-ffs">FFS</span><span className="ffs-next-brand-control">Control</span>
            </div>
            <span className="ffs-next-tagline">Are you ready to connect?</span>
          </div>
        </div>

        <nav className="ffsdev-tabs" aria-label="FFSControl sections">
        <div className="ffsdev-primary-tabs">
          {(['home', 'start-stream', 'obs', 'wam', 'sounds', 'chat-bot', 'themes'] as const).filter((id) => id !== 'themes' || role === 'owner').map((id) => {
            const Icon = ICONS[id];
            return (
              <button key={id} type="button" className={`ffsdev-tab ${activeRoute === id ? 'active' : ''}`}
                aria-current={activeRoute === id ? 'page' : undefined}
                onClick={() => onNavigate(id)}>
                <Icon size={16} /> {REBUILD_ROUTE_LABELS[id]}
              </button>
            );
          })}
          <details className="ffsdev-more" key={activeRoute}>
            <summary className={`ffsdev-tab ${!['home','start-stream','obs','wam','sounds','chat-bot','themes'].includes(activeRoute) ? 'active' : ''}`}>
              More <span aria-hidden="true">⌄</span>
            </summary>
            <div className="ffsdev-more-menu">
              {REBUILD_NAVIGATION.map((group) => {
                const items = group.items.filter((item) => !['home','start-stream','obs','wam','sounds','chat-bot','themes','commands','automations','loyalty','modules','weekend-prep'].includes(item.id) && (!item.ownerOnly || role === 'owner') && (role !== 'owner' || !['welcomes','alerts'].includes(item.id)));
                if (!items.length) return null;
                return <section key={group.label}><h2>{group.label}</h2>{items.map((item) => {
                  const Icon = ICONS[item.id];
                  return <button key={item.id} type="button" className={activeRoute === item.id ? 'active' : ''} onClick={() => onNavigate(item.id)}>
                    <Icon size={15} /> {item.label}
                  </button>;
                })}</section>;
              })}
            </div>
          </details>
        </div>
        <div className="ffsdev-tab-utilities">
          {sidebarUtility}
          <span className="ffsdev-tab-identity">{identityLabel}</span>
          {onLogout ? <button type="button" className="ffsdev-tab-signout" onClick={onLogout}>Sign out</button> : null}
        </div>
        </nav>
      </header>
      <div className="ffsdev-statusbar">
        <div className="ffs-next-live-strip">
          <span className={`ffs-next-live-state ${streaming ? 'live' : 'offline'}`}>
            {streaming ? 'LIVE' : 'OFFLINE'}
          </span>
          <button type="button" className="ffs-next-live-fact" onClick={() => onNavigate('obs')} title="Open OBS controls">
            <small>OBS</small>
            <strong>{currentScene || 'No scene'}</strong>
          </button>
          <button type="button" className="ffs-next-live-fact" onClick={() => onNavigate('wam')} title="Open WhatsAMusic">
            <small>WAM</small>
            <strong>{currentWamCategory || 'No category'}</strong>
          </button>
          <button type="button" className="ffs-next-live-fact" onClick={() => onNavigate('sounds')} title="Open Sounds">
            <small>Now playing</small>
            <strong>{nowPlaying || 'Nothing playing'}</strong>
          </button>
        </div>

        <div className="ffs-next-clock" aria-label={`${dateLabel} ${clockLabel}`}>
          <strong>{clockLabel}</strong>
          <span>{dateLabel}</span>
        </div>
      </div>
      <div className="ffs-next-body ffsdev-top-tab-body">
        <main className="ffs-next-main">
          <header className="ffs-next-page-heading">
            <div>
              <span>{activeGroup}</span>
              <h1>{activeTitle}</h1>
            </div>
            {activeRoute === 'wam' ? (
              <button
                type="button"
                className="ffs-button secondary ffs-next-page-action"
                disabled={!currentWamCategory?.trim()}
                onClick={() => void copyWamCategory()}
                title={currentWamCategory?.trim() ? 'Copy the current WAM category to the clipboard.' : 'Choose a WAM category first.'}
              >
                <Copy size={15} /> Copy to Clipboard
              </button>
            ) : null}
          </header>
          <div className="ffs-next-page-content" key={activeRoute}>{children}</div>
        </main>
      </div>
    </div>
  );
}
